<template>
  <modal
    effect="zoom"
    class="share-modal"
    :title="title"
    @hide="$emit('hideModal')"
  >
    <div class="share-modal__item-name">{{ name }}</div>
    <Comment
      class="share-modal__comment"
      :label="$t('common.say_something_about_this')"
      :comment="comment"
      @commentChanged="commentChanged"
    />
    <Alert
      :vuetifyProps="{
        color: 'blue',
        icon: 'mdi-information-outline',
        value: !selectedUsers.length,
      }"
      :text="bannerMessage"
    />
    <UserSelector
      class="share-modal__user-selector"
      :users="users"
      :label="$t('common.search')"
      :defaultUserImage="defaultUserImage"
      :emptyStateText="$t('common.no_results_found')"
      @onSearchChanged="onSearchChanged"
      @selectionChanged="selectionChanged"
    />
    <ModalFooter
      slot="modal-footer"
      okTestId="share_modal_ok"
      cancelTestId="share_modal_cancel"
      :okText="$t('common.button.share')"
      :cancelText="$t('common.button.cancel')"
      :disableOk="!selectedUsers.length"
      @cancel="$emit('hideModal')"
      @ok="share"
    />
  </modal>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapState } from 'vuex'

import mixin from '@/mixins/mixin.vue'
import defaultUserImage from '@/components/Core/UserSelector/assets/defaultUserProfilePhoto.svg'

import Modal from '@/components/Core/Modal.vue'
import ModalFooter from '@/components/Core/ModalFooter.vue'
import Comment from '@/components/Modals/ShareModal/Comment.vue'
import UserSelector from '@/components/Core/UserSelector/UserSelector.vue'
import Alert from '@/components/Alert.vue'

export default {
  name: 'shareModal',
  mixins: [mixin],
  props: {
    entityId: {
      type: String,
      required: true,
    },
    organizationId: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      required: false,
      default: '',
    },
    shareType: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    sharingAccountId: {
      type: String,
      required: true,
    },
    filters: {
      type: Object,
      required: false,
      default: () => {},
    },
  },

  data() {
    return {
      selectedUsers: [],
      comment: '',
      message: '',
      defaultUserImage,
    }
  },

  components: {
    Modal,
    ModalFooter,
    Comment,
    UserSelector,
    Alert,
  },

  computed: {
    ...mapState('shareModal', {
      users: state => {
        if (!state.accounts) return []
        return state.accounts.map(account => ({
          ...account,
          id: _.get(account, 'id', ''),
          name: account?.displayname
            ? `${account.displayname ?? ''} ${account?.familyname ?? ''}`
            : _.get(account, 'email'),
          avatarMediaObject: _.get(account, 'photo', null),
          position: _.get(account, 'position', ''),
        }))
      },
    }),
    bannerMessage() {
      switch (this.shareType) {
        case 'Challenge':
          return ` ${this.$t(
            'validation.you_need_at_least_select_one_user_to_share_this_challenge'
          )}`
        default:
          return ` ${this.$t(
            'validation.you_need_at_least_select_one_user_to_share_this_rev'
          )}`
      }
    },
  },

  methods: {
    ...mapActions({
      fetchUsers: 'shareModal/fetchUsers',
      shareItem: 'shareModal/shareItem',
    }),

    commentChanged(newComment) {
      this.comment = newComment
    },

    selectionChanged(selectedUsers) {
      this.selectedUsers = selectedUsers
    },

    onSearchChanged(searchTerms) {
      this.fetchUsers({
        organizationId: this.organizationId,
        searchTerms: searchTerms,
        filters: this.filters,
      })
    },
    getSuccessMessageByType() {
      switch (this.shareType) {
        case 'Challenge':
          return this.$t('notifications.shared_challenge_successfully')
        default:
          return this.$t('notifications.shared_rev_successfully')
      }
    },

    share() {
      const data = {
        organizationId: this.getOrganizationId(),
        sharedBy: this.sharingAccountId,
        sharedTo: this.selectedUsers.map(user => user.id),
        message: this.comment,
        sharedObjectId: this.entityId,
        sharedObjectType: this.shareType,
      }

      if (_.isEmpty(this.selectedUsers)) return
      const successMessage = this.getSuccessMessageByType()
      this.shareItem(data)
        .then(() => {
          this.$emit('hideModal')
          this.showSuccess(successMessage)
        })
        .catch(() => {
          this.$emit('hideModal')
          this.showError(this.$t('error.shared_rev_failed'))
        })
    },
  },

  mounted() {
    this.fetchUsers({
      organizationId: this.organizationId,
      filters: this.filters,
    })
  },
}
</script>
<style lang="scss">
.share-modal {
  .modal-title {
    overflow-wrap: break-word;
  }

  &__item-name {
    overflow-wrap: break-word;
  }
}
.share-modal__comment {
  padding-top: 16px;
}
.share-modal-modal {
  button.close {
    display: none;
  }

  .modal-body {
    padding-top: 10px;
    padding-bottom: 20px;
  }

  .share-modal__users-list {
    margin-top: 20px;
    padding-right: 35px;
    padding-bottom: 20px;
  }

  .modal-footer {
    .share-modal__footer {
      .cancel {
        color: #555555;
        border: 1px solid #555555;
        border-radius: 4px;
        font-weight: bold;
      }
      .btn-primary {
        font-weight: bold;
        &:disabled {
          background-color: #b6dafd;
        }
      }
    }
  }
}
</style>
